import { useEffect, useState } from "react";
import { Account } from "../../../models/account";
import { Document } from "../../../models/document";
import { dateToString, getCustomDataValue, getValueByPath } from "@shared/lib/utils";
import { Item } from "../../../models/item";
import { Work } from "../../../models/work";
import { Car } from "../../../models/car";
import dayjs from "dayjs";
import { useErrorBoundary } from "react-error-boundary";
import { handleErrorWithBoundary } from "@shared/lib/errors"
import { addShekelSymbol } from "./PrintFieldValue.utils";
import { SelectTable, Table } from "../../../models/table";
import { ManufacturerModel } from "../../../models/manufacturer-model";
import { useSelectTableByNumber } from "../../lib/hooks/useSelectTableByNumber";
import { useLazyGetManufacturerModelsByIdQuery, useLazyGetTableOptionsByTableIndexQuery } from "../../../features/tables/api/systemTables.api";
import { DocumentStructureHebType } from "../../../models/types";

type FieldStructure = {
    field_name: string,
    field_type: number,
    field_table: number | null,
    type?: DocumentStructureHebType
}

interface PrintFieldValueProps<F extends FieldStructure, T extends Account | Document | Item | Work | Car> {
    field: F;
    accountData: T;
    prefix?: string | undefined;
}


//print data of account by field object.

function PrintFieldValue<F extends FieldStructure, T extends Account | Document | Item | Work | Car>({ field, accountData, prefix }: PrintFieldValueProps<F, T>) {

    // variables

    const { showBoundary } = useErrorBoundary();

    const customDataMatch = field.field_name.match(/^custom_data\.(\d+)\.field_value$/);
    const customDataIndex = customDataMatch ? Number(customDataMatch[1]) : null;
    const customDataPrefix = prefix ? prefix.replace(/\.$/, "") : undefined;

    let value =
        customDataIndex !== null
            ? getCustomDataValue(accountData as Record<string, any>, customDataIndex, customDataPrefix)
            : getValueByPath(accountData, prefix? `${prefix}.${field.field_name}` : field.field_name);
    value = addShekelSymbol(field.field_name, value);

    const [getSelectTableData] = useSelectTableByNumber();
    const [getSystemTableData] = useLazyGetTableOptionsByTableIndexQuery();
    const [getManufacturerModels] = useLazyGetManufacturerModelsByIdQuery();
    
    const [table, setTable] = useState<SelectTable[] | undefined>();
    const [models, setModels] = useState<ManufacturerModel[] | undefined>();


    // run: once, in component creation
    useEffect(() => {
        void getComponentData().catch((error) => {
            handleErrorWithBoundary(error, showBoundary);
        });
    }, []);

    /**
     * if the type is select input, get select options.
     * set "table" state with data.
     */
    async function getComponentData(): Promise<void>{
        setSelectFieldOptions();
        setManufacturerModelsOptions();
    }


    /**
     * if: it's select field
     * do: set field options with data.
     */
    async function setSelectFieldOptions(): Promise<void>{
        if ((field.field_type === 4 || field.field_type === 5) &&  field.field_table && !table) {
            setTable(await getSelectTableData(field.field_table));
        }
    }
        

    /**
     * if: this is select model field
     * do: set model options by manufacturer chosen
     */
    async function setManufacturerModelsOptions(): Promise<void>{
        if (field.field_type === 6) {
            
            const manufacturersTable: Table[] = await getSystemTableData(1, true).unwrap();

            const manufacturerIndex = (accountData as Car).manufacturer ?? (accountData as Document).car?.manufacturer;

            if(manufacturerIndex){
                const manufacturerID = manufacturersTable[manufacturerIndex - 1].id;
                setModels(await getManufacturerModels(manufacturerID, true).unwrap());
            }

        }
    }

    /**
     * @param fieldType 
     * @param fieldName 
     * @returns the field name with prefix
     */
    function setPrefix(fieldType: DocumentStructureHebType | undefined, fieldName: string): string {
        
        if(!fieldType) return fieldName;

        let prefix;

        switch (fieldType) {
            case "חשבון":
                prefix = "document_account.";
                break;

            case "רכב":
                prefix = "car.";
                break;
            
            case "עבודה":
                prefix = "work.";
                break;

            case "פריט":
                prefix = "item.";
                break;
        
            default:
                prefix = ""
                break;
        }

        return `${prefix}${fieldName}`;
    }


    if (field.field_type === 1 || field.field_type === 2) { // en/heb
        return <>{value as string}</>
    }

    if (field.field_type === 3) { //date
        const date = dayjs(value as string);
        return <>{dateToString(date.toDate())}</>
    }

    if (field.field_type === 4) { //select
        const textValue = table?.find(table => table.index === value)?.name;
        return textValue ? <>{textValue}</> : <></>;
    }

    if (field.field_type === 5) { //select
        return <>{value}</>
    }

    if (field.field_type === 6) { //model

        const textValue = models?.find(model => model.index === value)?.name;
        return textValue ? <>{textValue}</> : <></>;
    }

    if (field.field_type === 7) { //number
        return <>{value}</>
    }


    if (field.field_type === 8) {
        return <>{(value as number)?.toFixed(2)}</>
    }

    if (field.field_type === 21) { //boolean
        return <>{value ? "כן" : "לא"}</>
    }

    return (
        <>PrintInputValue</>
    )
}

export default PrintFieldValue